/**
 * SCR-011 — Class detail: 4 tabs — Thông tin, Lịch học, Giáo viên, Sĩ số.
 * Roles: admin (all classes), manager (own branch).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { AlertCircle, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ClassForm, type ClassFormValues } from '@client/components/forms/class-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  classKeys,
  useClass,
  useClassEnrollments,
  useClassSchedules,
  useClassTeachers,
  useUpdateClass,
  type ClassSchedule,
  type ClassTeacher,
  type Weekday,
  type ScheduleMode,
} from '@client/lib/queries/use-classes';
import { useCourses } from '@client/lib/queries/use-courses';
import { useRooms } from '@client/lib/queries/use-rooms';
import { api } from '@client/lib/api-client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export const Route = createFileRoute('/admin/classes/$classId')({
  component: ClassDetailPage,
});

const STATUS_META: Record<string, { label: string; variant: 'warning' | 'info' | 'success' | 'secondary' | 'destructive' }> = {
  planned:   { label: 'Chờ khai giảng', variant: 'warning' },
  open:      { label: 'Mở ghi danh',    variant: 'info' },
  running:   { label: 'Đang học',        variant: 'success' },
  finished:  { label: 'Đã kết thúc',    variant: 'secondary' },
  cancelled: { label: 'Đã hủy',         variant: 'destructive' },
};

const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Thứ Hai', tue: 'Thứ Ba', wed: 'Thứ Tư',
  thu: 'Thứ Năm', fri: 'Thứ Sáu', sat: 'Thứ Bảy', sun: 'Chủ Nhật',
};

// ─── Sub-components ────────────────────────────────────────────────────────

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
      <span className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}

// ─── Schedule dialog ────────────────────────────────────────────────────────

interface ScheduleFormState {
  mode: ScheduleMode;
  weekday: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  roomId: string;
}

function ScheduleDialog({
  classId,
  branchId,
  schedule,
  rooms,
  open,
  onClose,
}: {
  classId: string;
  branchId: string;
  schedule: ClassSchedule | null;
  rooms: { id: string; name: string }[];
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!schedule;
  const [form, setForm] = useState<ScheduleFormState>({
    mode: schedule?.mode ?? 'weekly',
    weekday: schedule?.weekday ?? '',
    sessionDate: schedule?.sessionDate ?? '',
    startTime: schedule?.startTime ?? '',
    endTime: schedule?.endTime ?? '',
    roomId: schedule?.roomId ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function handleModeChange(mode: ScheduleMode) {
    setForm((f) => ({ ...f, mode, weekday: '', sessionDate: '' }));
  }

  async function handleSave() {
    setError(null);
    if (!form.startTime || !form.endTime) { setError('Cần nhập giờ bắt đầu và kết thúc.'); return; }
    if (form.endTime <= form.startTime) { setError('Giờ kết thúc phải sau giờ bắt đầu.'); return; }
    if (form.mode === 'weekly' && !form.weekday) { setError('Chọn thứ trong tuần.'); return; }
    if (form.mode === 'single' && !form.sessionDate) { setError('Chọn ngày diễn ra.'); return; }
    setSaving(true);
    try {
      const payload = {
        branchId,
        mode: form.mode,
        weekday: form.mode === 'weekly' ? form.weekday : undefined,
        sessionDate: form.mode === 'single' ? form.sessionDate : undefined,
        startTime: form.startTime,
        endTime: form.endTime,
        roomId: form.roomId || undefined,
      };
      if (isEdit && schedule) {
        await api.patch(`/api/classes/${classId}/schedules/${schedule.id}`, payload);
        toast.success('Đã cập nhật buổi học.');
      } else {
        await api.post(`/api/classes/${classId}/schedules`, payload);
        toast.success('Đã thêm buổi học.');
      }
      await qc.invalidateQueries({ queryKey: classKeys.schedules(classId) });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Sửa buổi học' : 'Thêm buổi học'}</DialogTitle>
        </DialogHeader>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            {(['weekly', 'single'] as ScheduleMode[]).map((m) => (
              <Button
                key={m}
                type="button"
                variant={form.mode === m ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModeChange(m)}
              >
                {m === 'weekly' ? 'Hằng tuần' : 'Buổi đơn'}
              </Button>
            ))}
          </div>

          {/* Weekday or date */}
          {form.mode === 'weekly' ? (
            <div className="flex flex-col gap-1.5">
              <Label>Thứ trong tuần <span className="text-destructive">*</span></Label>
              <Select value={form.weekday} onValueChange={(v) => setForm((f) => ({ ...f, weekday: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn thứ" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(WEEKDAY_LABELS) as [Weekday, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label>Ngày diễn ra <span className="text-destructive">*</span></Label>
              <Input
                type="date"
                value={form.sessionDate}
                onChange={(e) => setForm((f) => ({ ...f, sessionDate: e.target.value }))}
              />
            </div>
          )}

          {/* Time range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Giờ bắt đầu <span className="text-destructive">*</span></Label>
              <Input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Giờ kết thúc <span className="text-destructive">*</span></Label>
              <Input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
              />
            </div>
          </div>

          {/* Room override */}
          {rooms.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label>Phòng (override)</Label>
              <Select
                value={form.roomId ? form.roomId : '__none__'}
                onValueChange={(v) => setForm((f) => ({ ...f, roomId: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Dùng phòng mặc định lớp" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Dùng phòng mặc định</SelectItem>
                  {rooms.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Đang lưu...' : 'Lưu'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Assign teacher dialog ───────────────────────────────────────────────────

function AssignTeacherDialog({
  classId,
  branchId,
  open,
  onClose,
}: {
  classId: string;
  branchId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [teacherId, setTeacherId] = useState('');
  const [role, setRole] = useState<'lead' | 'assistant'>('lead');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Use useQuery to load teachers — valid hook call at component level
  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: ['staff', 'teachers', branchId],
    queryFn: () =>
      api.get<{ items: { id: string; fullName: string }[] }>('/api/staff', {
        role: 'teacher',
        branchId,
        isActive: 'true',
        pageSize: '100',
      }),
    enabled: open,
  });
  const staffList = staffData?.items ?? [];

  async function handleSave() {
    if (!teacherId) { setError('Chọn giáo viên.'); return; }
    setError(null);
    setSaving(true);
    try {
      await api.post(`/api/classes/${classId}/teachers`, { teacherId, role, branchId });
      await qc.invalidateQueries({ queryKey: classKeys.teachers(classId) });
      toast.success('Đã xếp giáo viên.');
      onClose();
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setError('Giáo viên đã được phân công.');
      } else {
        setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle>Xếp giáo viên</DialogTitle></DialogHeader>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Giáo viên <span className="text-destructive">*</span></Label>
            {staffLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn giáo viên" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.fullName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Vai trò</Label>
            <Select value={role} onValueChange={(v) => setRole(v as 'lead' | 'assistant')}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">GV phụ trách</SelectItem>
                <SelectItem value="assistant">GV hỗ trợ</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Đang lưu...' : 'Xếp'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Enroll student dialog ───────────────────────────────────────────────────

function EnrollStudentDialog({
  classId,
  branchId,
  open,
  onClose,
}: {
  classId: string;
  branchId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [overCapacityWarning, setOverCapacityWarning] = useState(false);

  // useQuery at component level — correct hook usage
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ['students', 'active', branchId],
    queryFn: () =>
      api.get<{ items: { id: string; studentCode: string; fullName: string }[] }>('/api/students', {
        branchId,
        status: 'active',
        pageSize: '200',
      }),
    enabled: open,
  });
  const students = studentsData?.items ?? [];

  async function handleSave(confirm = false) {
    if (!studentId) { setError('Chọn học viên.'); return; }
    setError(null);
    setSaving(true);
    try {
      await api.post('/api/enrollments', {
        studentId,
        classId,
        note: note || undefined,
        confirmOverCapacity: confirm || undefined,
      });
      await qc.invalidateQueries({ queryKey: classKeys.enrollments(classId) });
      await qc.invalidateQueries({ queryKey: classKeys.detail(classId) });
      toast.success('Đã ghi danh học viên.');
      onClose();
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setError('Học viên đã ghi danh lớp này.');
      } else if (isApiError(err) && err.status === 422) {
        const body = err.body as { warning?: string; currentEnrollment?: number; capacity?: number };
        if (body?.warning === 'over_capacity') {
          setOverCapacityWarning(true);
          setError(`Lớp đã đủ sĩ số (${body.currentEnrollment}/${body.capacity}). Xác nhận để ghi danh vượt sĩ số?`);
        } else {
          setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
        }
      } else {
        setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle>Ghi danh học viên</DialogTitle></DialogHeader>
        {error && (
          <Alert variant={overCapacityWarning ? 'default' : 'destructive'}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Học viên <span className="text-destructive">*</span></Label>
            {studentsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn học viên" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.fullName} ({s.studentCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Ghi chú</Label>
            <Textarea
              rows={2}
              maxLength={300}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
            {overCapacityWarning ? (
              <Button
                variant="destructive"
                onClick={() => void handleSave(true)}
                disabled={saving}
              >
                Xác nhận vượt sĩ số
              </Button>
            ) : (
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Đang lưu...' : 'Ghi danh'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

function ClassDetailPage() {
  const { classId } = Route.useParams();
  const qc = useQueryClient();

  const { data: cls, isLoading, isError } = useClass(classId);
  const updateClass = useUpdateClass(classId);
  const { data: schedulesData } = useClassSchedules(classId);
  const { data: teachersData } = useClassTeachers(classId);
  const { data: enrollmentsData } = useClassEnrollments(classId);
  const { data: coursesData } = useCourses({ isActive: true, pageSize: 100 });
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const { data: roomsData } = useRooms({
    branchId: cls?.branchId,
    isActive: true,
    pageSize: 100,
  });

  const schedules = schedulesData ?? [];
  const teachers = teachersData ?? [];
  const enrollments = enrollmentsData?.items ?? [];
  const rooms = roomsData?.items ?? [];
  const courses = coursesData?.items ?? [];
  const branches = branchesData?.items ?? [];

  const [editOpen, setEditOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editSchedule, setEditSchedule] = useState<ClassSchedule | null>(null);

  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);

  async function handleClassSubmit(values: ClassFormValues) {
    setFormError(null);
    try {
      const payload = {
        ...values,
        roomId: values.roomId || undefined,
        endDate: values.endDate || undefined,
        note: values.note || undefined,
      };
      await updateClass.mutateAsync(payload);
      toast.success('Đã cập nhật lớp học.');
      setEditOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const body = err.body as { message?: string; activeEnrollmentCount?: number };
        const count = body?.activeEnrollmentCount;
        setFormError(
          count
            ? `Không thể hủy — còn ${count} học viên đang ghi danh.`
            : (body?.message ?? 'Không thể thực hiện.'),
        );
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
      }
    }
  }

  async function handleRemoveTeacher(entry: ClassTeacher) {
    if (!confirm('Gỡ giáo viên này khỏi lớp?')) return;
    try {
      await api.delete(`/api/classes/${classId}/teachers/${entry.id}`);
      await qc.invalidateQueries({ queryKey: classKeys.teachers(classId) });
      toast.success('Đã gỡ giáo viên.');
    } catch {
      toast.error('Không thể gỡ giáo viên.');
    }
  }

  async function handleDeleteSchedule(s: ClassSchedule) {
    if (!confirm('Xóa buổi học này?')) return;
    try {
      await api.delete(`/api/classes/${classId}/schedules/${s.id}`);
      await qc.invalidateQueries({ queryKey: classKeys.schedules(classId) });
      toast.success('Đã xóa buổi học.');
    } catch {
      toast.error('Không thể xóa buổi học.');
    }
  }

  if (isLoading) return <ClassDetailSkeleton />;

  if (isError || !cls) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Không tải được thông tin lớp học.{' '}
          <Link to="/admin/classes" className="underline">Quay lại</Link>
        </AlertDescription>
      </Alert>
    );
  }

  const statusMeta = STATUS_META[cls.status] ?? STATUS_META['planned'];
  const isCancelled = cls.status === 'cancelled';
  const weeklySchedules = schedules.filter((s) => s.mode === 'weekly');
  const singleSchedules = schedules.filter((s) => s.mode === 'single');

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/classes">Lớp học</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{cls.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-foreground">{cls.name}</h1>
          <Badge variant={statusMeta?.variant} className="text-xs">
            {statusMeta?.label}
          </Badge>
          {isCancelled && (
            <Badge variant="destructive" className="text-xs">Lớp đã hủy</Badge>
          )}
        </div>
        {!isCancelled && (
          <Button
            className="self-start sm:self-auto"
            onClick={() => { setFormError(null); setEditOpen(true); }}
          >
            <Pencil className="h-4 w-4 mr-1.5" /> Sửa
          </Button>
        )}
      </div>

      {/* Tabs — vertical rail (mobile-first: stacks above content <768px) */}
      <Tabs
        defaultValue="info"
        orientation="vertical"
        className="flex-col gap-4 md:flex-row md:gap-6"
      >
        <TabsList
          variant="line"
          className="w-full shrink-0 md:w-48 md:border-r md:pr-2"
        >
          <TabsTrigger value="info">Thông tin</TabsTrigger>
          <TabsTrigger value="schedule">Lịch học</TabsTrigger>
          <TabsTrigger value="teachers">Giáo viên</TabsTrigger>
          <TabsTrigger value="roster">Sĩ số</TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Info ── */}
        <TabsContent value="info" className="mt-0">
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <InfoRow label="Mã lớp" value={cls.code} mono />
                <InfoRow label="Khóa học" value={cls.courseName ?? ''} />
                <InfoRow label="Cơ sở" value={cls.branchName ?? ''} />
                <InfoRow label="Phòng mặc định" value={cls.roomName ?? ''} />
                <InfoRow label="Sĩ số" value={`${cls.currentEnrollment}/${cls.capacity}`} />
                <InfoRow
                  label="Khai giảng"
                  value={cls.startDate ? format(new Date(cls.startDate), 'dd/MM/yyyy') : ''}
                />
                <InfoRow
                  label="Bế giảng"
                  value={cls.endDate ? format(new Date(cls.endDate), 'dd/MM/yyyy') : ''}
                />
                {cls.note && (
                  <div className="md:col-span-2 flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Ghi chú</span>
                    <p className="text-sm">{cls.note}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Schedule ── */}
        <TabsContent value="schedule" className="mt-0">
          <div className="flex flex-col gap-4">
            {!isCancelled && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => { setEditSchedule(null); setScheduleDialogOpen(true); }}
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Thêm buổi
                </Button>
              </div>
            )}

            {schedules.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">Chưa có buổi học.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {weeklySchedules.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Lịch hằng tuần</h3>
                    <div className="flex flex-col gap-2">
                      {weeklySchedules.map((s) => (
                        <ScheduleRow
                          key={s.id}
                          schedule={s}
                          onEdit={() => { setEditSchedule(s); setScheduleDialogOpen(true); }}
                          onDelete={() => void handleDeleteSchedule(s)}
                          disabled={isCancelled}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {singleSchedules.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Buổi đơn</h3>
                    <div className="flex flex-col gap-2">
                      {singleSchedules.map((s) => (
                        <ScheduleRow
                          key={s.id}
                          schedule={s}
                          onEdit={() => { setEditSchedule(s); setScheduleDialogOpen(true); }}
                          onDelete={() => void handleDeleteSchedule(s)}
                          disabled={isCancelled}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Tab 3: Teachers ── */}
        <TabsContent value="teachers" className="mt-0">
          <div className="flex flex-col gap-4">
            {!isCancelled && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setTeacherDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-1.5" /> Xếp giáo viên
                </Button>
              </div>
            )}
            {teachers.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">Chưa xếp giáo viên.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {teachers.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between rounded-lg border p-3 gap-3"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-sm">{t.teacherName ?? t.teacherId}</span>
                      <span className="text-xs text-muted-foreground">
                        {t.role === 'lead' ? 'GV phụ trách' : 'GV hỗ trợ'}
                        {t.staffCode ? ` · ${t.staffCode}` : ''}
                        {t.assignedAt ? ` · ${format(new Date(t.assignedAt), 'dd/MM/yyyy')}` : ''}
                      </span>
                    </div>
                    {!isCancelled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => void handleRemoveTeacher(t)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Tab 4: Roster ── */}
        <TabsContent value="roster" className="mt-0">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">
                Sĩ số:{' '}
                <span className={cls.currentEnrollment >= cls.capacity ? 'text-destructive font-semibold' : 'font-semibold'}>
                  {cls.currentEnrollment}/{cls.capacity}
                </span>
              </span>
              {!isCancelled && (
                <Button size="sm" onClick={() => setEnrollDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Ghi danh
                </Button>
              )}
            </div>
            {enrollments.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">Chưa có học viên.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {enrollments.map((e) => (
                  <div key={e.id} className="flex items-center justify-between rounded-lg border p-3 gap-3">
                    <div className="flex flex-col gap-0.5">
                      <Link
                        to="/admin/students/$studentId"
                        params={{ studentId: e.studentId }}
                        className="font-medium text-sm hover:underline text-primary"
                      >
                        {e.studentName ?? e.studentId}
                      </Link>
                      <span className="text-xs text-muted-foreground font-mono">
                        {e.studentCode}
                        {e.enrolledAt ? ` · ${format(new Date(e.enrolledAt), 'dd/MM/yyyy')}` : ''}
                      </span>
                    </div>
                    <Badge
                      className={
                        e.status === 'active'
                          ? 'bg-brand-green-100 text-brand-green-700 border-0 text-xs'
                          : 'text-xs'
                      }
                      variant={e.status === 'active' ? undefined : 'secondary'}
                    >
                      {e.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit class dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sửa lớp học</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <ClassForm
            isEdit
            isSubmitting={updateClass.isPending}
            branches={branches}
            courses={courses.map((c) => ({ id: c.id, code: c.code, name: c.name, level: c.level }))}
            rooms={rooms.map((r) => ({ id: r.id, name: r.name }))}
            defaultValues={{
              code: cls.code,
              name: cls.name,
              courseId: cls.courseId,
              branchId: cls.branchId,
              roomId: cls.roomId ?? '',
              status: cls.status,
              startDate: cls.startDate ?? '',
              endDate: cls.endDate ?? '',
              capacity: cls.capacity,
              note: cls.note ?? '',
            }}
            onSubmit={handleClassSubmit}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <ScheduleDialog
        classId={classId}
        branchId={cls.branchId}
        schedule={editSchedule}
        rooms={rooms.map((r) => ({ id: r.id, name: r.name }))}
        open={scheduleDialogOpen}
        onClose={() => { setScheduleDialogOpen(false); setEditSchedule(null); }}
      />

      {/* Assign teacher dialog */}
      <AssignTeacherDialog
        classId={classId}
        branchId={cls.branchId}
        open={teacherDialogOpen}
        onClose={() => setTeacherDialogOpen(false)}
      />

      {/* Enroll student dialog */}
      <EnrollStudentDialog
        classId={classId}
        branchId={cls.branchId}
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
      />
    </div>
  );
}

function ScheduleRow({
  schedule,
  onEdit,
  onDelete,
  disabled,
}: {
  schedule: ClassSchedule;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}) {
  const label =
    schedule.mode === 'weekly'
      ? (schedule.weekday ? WEEKDAY_LABELS[schedule.weekday] : schedule.weekday ?? '')
      : schedule.sessionDate
        ? format(new Date(schedule.sessionDate), 'dd/MM/yyyy')
        : '';

  return (
    <div className="flex items-center justify-between rounded-lg border p-3 gap-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          {schedule.startTime} – {schedule.endTime}
          {schedule.roomName ? ` · ${schedule.roomName}` : ''}
        </span>
      </div>
      {!disabled && (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function ClassDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-10 w-full" />
      <Card>
        <CardContent className="grid grid-cols-2 gap-3 pt-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
